'use client';

/**
 * Ads Assistant — an agentic chat for one Meta ad account.
 *
 * It drives a dedicated gateway session (`ads-asst-<accountId>`) opened with
 * cwd `~/beast`, so the agent inherits the project-scoped `meta-ads` MCP and can
 * pull live campaign data (or curl the local Beast API) while it reasons. The
 * on-screen audit is handed to the agent as model-only grounding on the first
 * turn via the gateway's `context` field, so its first reply already knows the
 * account's numbers without echoing them back.
 *
 * The chat frame itself (session open, transcript, streaming, input row,
 * suggestion chips) lives in the shared AssistantShell — this file only
 * supplies the ads-specific identity and grounding.
 */
import { AssistantShell } from '@/components/chat/AssistantShell';
import type { Audit } from '@/lib/ads-types';

const SUGGESTIONS = [
  'Where do we stand — top 3 things to fix this week?',
  'Which ad should I scale, and which should I pause?',
  'Is my cost per result healthy? How do I bring it down?',
  'What audience or placement is wasting spend?',
];

/** Compact, model-only grounding for the first turn of a fresh session. */
function buildGrounding(account: { id: string; name: string; currency: string }, audit: Audit | null, rangeLabel: string): string {
  const lines: string[] = [];
  lines.push(`You are a senior Meta (Facebook/Instagram) ads strategist embedded in the Beast dashboard, advising on ONE ad account. Be concise, specific and actionable — talk in concrete numbers and next steps, not generic theory. Use short markdown (bullets, bold key numbers). Ask before assuming budgets.`);
  lines.push('');
  lines.push(`Account: ${account.name} (id ${account.id}) · currency ${account.currency} · window: ${rangeLabel}`);
  if (audit) {
    const t = audit.totals;
    lines.push(`Current audit score: ${Math.round(audit.score)}/100.`);
    lines.push(`Totals: spend ${audit.account.currency} ${t.spend.toFixed(2)}, impressions ${t.impressions}, clicks ${t.clicks}, CTR ${t.ctr.toFixed(2)}%, CPC ${t.cpc.toFixed(2)}, CPM ${t.cpm.toFixed(2)}${t.frequency ? `, freq ${t.frequency.toFixed(2)}` : ''}.`);
    if (audit.conversions?.length) {
      lines.push(`Conversions: ${audit.conversions.map((c) => `${c.name} ${c.count} @ ${c.costPer.toFixed(2)}/result`).join('; ')}.`);
    }
    if (audit.findings?.length) {
      lines.push(`Findings: ${audit.findings.slice(0, 6).map((f) => `[${f.severity}] ${f.title}`).join('; ')}.`);
    }
    if (audit.recommendations?.length) {
      lines.push(`Existing recs: ${audit.recommendations.slice(0, 6).map((r) => `[${r.priority}] ${r.title}`).join('; ')}.`);
    }
    if (audit.ads?.length) {
      const ads = [...audit.ads].sort((a, b) => (b.spend || 0) - (a.spend || 0)).slice(0, 8);
      lines.push(`Ads (name · spend · CTR · CPC${audit.primaryConversion ? ` · ${audit.primaryConversion.name}/cost` : ''}): ` +
        ads.map((a) => `${a.name} ${a.spend.toFixed(0)} ${a.ctr.toFixed(2)}% ${a.cpc.toFixed(2)}${a.convCount != null ? ` ${a.convCount}/${a.convCostPer?.toFixed(2) ?? '—'}` : ''}`).join(' | '));
    }
  } else {
    lines.push(`No saved audit is loaded for this window yet — pull the data yourself before advising.`);
  }
  lines.push('');
  lines.push(`To get fresh/live numbers you have two options: (1) the meta-ads MCP tools (mcp__meta-ads__*) — use account id ${account.id}; or (2) curl the local Beast API on this machine, e.g. \`curl -s "http://localhost:3000/api/ads/audit/${account.id}?range=last_30d"\` and \`curl -s "http://localhost:3000/api/ads/insights/${account.id}?range=last_30d"\` (also supports ?since=YYYY-MM-DD&until=YYYY-MM-DD). Do NOT change/pause/create any campaign, ad set or ad unless the user explicitly asks you to.`);
  return lines.join('\n');
}

export function AdsAssistant({ accountId, accountName, currency, audit, rangeLabel }: {
  accountId: string;
  accountName: string;
  currency: string;
  audit: Audit | null;
  rangeLabel: string;
}) {
  return (
    <AssistantShell
      slug={`ads-asst-${accountId}`}
      sessionName={`Ads · ${accountName}`}
      sessionIcon="📈"
      title="Ads Assistant"
      subject={accountName}
      emptyGradient="from-cyan-500 to-blue-600"
      emptyBlurb="I can read this account's live data, dig into any campaign, and recommend what to change."
      suggestions={SUGGESTIONS}
      buildGrounding={() => buildGrounding({ id: accountId, name: accountName, currency }, audit, rangeLabel)}
      confirmClearText={`Clear this assistant chat for ${accountName} and start fresh?`}
    />
  );
}
